import { Component, Input, OnInit, Output, EventEmitter, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, ValidationErrors, FormsModule } from '@angular/forms';


import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OnboardingService, Board } from '../../../services/onboarding/onboarding.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';
import { isFieldInvalid,validateFile } from '../../shared/validators/form-validation.utils';
import { LinkdindetailsFetchComponent } from '../../../student-portal/application-form/sections/linkdindetails-fetch/linkdindetails-fetch.component';

const yearRangeValidator = (group: AbstractControl): ValidationErrors | null => {
  const fromCtrl = group.get('yearsFrom');
  const toCtrl = group.get('yearsTo');
  if (!fromCtrl || !toCtrl) return null;

  const fromVal = fromCtrl.value;
  const toVal = toCtrl.value;
  const currentYear = new Date().getFullYear();

  if (fromVal && /^\d{4}$/.test(fromVal)) {
    const fromYear = parseInt(fromVal, 10);
    if (fromYear > currentYear) {
      fromCtrl.setErrors({ ...fromCtrl.errors, yearFuture: true });
    } else {
      if (fromCtrl.hasError('yearFuture')) {
        const errs = { ...fromCtrl.errors };
        delete errs['yearFuture'];
        fromCtrl.setErrors(Object.keys(errs).length ? errs : null);
      }
    }

    if (toVal && /^\d{4}$/.test(toVal)) {
      const toYear = parseInt(toVal, 10);
      if (toYear < fromYear) {
        toCtrl.setErrors({ ...toCtrl.errors, yearRange: true });
      } else {
        if (toCtrl.hasError('yearRange')) {
          const errs = { ...toCtrl.errors };
          delete errs['yearRange'];
          toCtrl.setErrors(Object.keys(errs).length ? errs : null);
        }
      }
    }
  }
  return null;
};


export interface DocumentParseData {
  jobId: string;
  status: 'pending' | 'parsing' | 'completed' | 'error';
  extractedData?: any;
  pollCount?: number;
}

@Component({
  selector: 'app-da-education',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    DropdownModule,
    FieldErrorComponent,
    LinkdindetailsFetchComponent
  ],
  providers: [MessageService],
  templateUrl: './education.component.html',
  styleUrl: './education.component.scss'
})
export class DaEducationComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;

  @ViewChild(LinkdindetailsFetchComponent) docReadingModal!: LinkdindetailsFetchComponent;
  isDocModalVisible: boolean = false;
  docModalFileName: string = '';
  activeDocModalIndex: number = -1;
  pendingExtractedData: any = null;
  parseJobData: DocumentParseData[] = [];
  private pollTimeouts: any[] = [];

  @Output() loaded = new EventEmitter<void>();

  displayPreviewDialog: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  originalFileUrl: string = '';
  isPdfLoading: boolean = false;
  isSavingMap: { [key: number]: boolean } = {};

  // Delete dialog state
  showDeleteDialog = false;
  isDeleting = false;
  pendingDeleteIndex: number | null = null;

  // Boards & Countries dropdown data
  boardOptions: { label: string; value: number }[] = [];
  countryOptions: { label: string; value: string }[] = [];

  // Subject tag management: per-item temp state
  subjectInputs: { [key: number]: string } = {};
  scoreInputs: { [key: number]: string } = {};
  // Array of subject tags per education item: { name, score }[]
  subjectTags: { [key: number]: { name: string; score: string }[] } = {};
  // Snapshot of original tags to detect changes
  subjectTagsOriginal: { [key: number]: string } = {};


  allLevelValues = [0, 1, 2, 3, 4]; // Grade 10, Grade 11, Grade 12, Bachelors, Masters

  levelOptions = [
    { label: 'Grade 10', value: 0 },
    { label: 'Grade 11', value: 1 },
    { label: 'Grade 12', value: 2 },
    { label: 'Bachelors', value: 3 },
    { label: 'Masters', value: 4 }
  ];

  get usedLevels(): number[] {
    return this.education.controls
      .map(c => c.get('level')?.value)
      .filter(v => v !== null && v !== undefined);
  }

  get availableLevelOptions() {
    return this.levelOptions.filter(opt => !this.usedLevels.includes(opt.value));
  }

  get allLevelsAdded(): boolean {
    return this.allLevelValues.every(v => this.usedLevels.includes(v));
  }

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private utilityService: UtilityService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    if (!this.parentForm.get('education')) {
      this.parentForm.addControl('education', this.fb.array([]));
    }

    this.loadBoards();
    this.loadCountries();
    this.loadEducationData();
  }

  get education(): FormArray {
    return this.parentForm.get('education') as FormArray;
  }

  loadBoards() {
    this.onboardingService.getBoards().subscribe({
      next: (boards: Board[]) => {
        this.boardOptions = boards.map(b => ({ label: b.name, value: b.id }));
      },
      error: () => {
        this.boardOptions = [];
      }
    });
  }

  loadCountries() {
    this.utilityService.getCountries().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.result || []);
        this.countryOptions = list.map((c: any) => ({
          label: c.name,
          value: c.name
        }));
      },
      error: () => {
        this.countryOptions = [];
      }
    });
  }

  loadEducationData() {
    this.onboardingService.getEducations().subscribe({
      next: (res: any) => {
        const results = res.result || res.Result || [];
        if (results.length > 0) {
          this.education.clear();
          results.forEach((record: any) => {
            this.addEducation(record);
          });
        } else {
          if (this.education.length === 0) {
            this.addEducation();
          }
        }
        this.loaded.emit();
      },
      error: (err) => {
        console.warn('Failed to load education details:', err);
        if (this.education.length === 0) {
          this.addEducation();
        }
        this.loaded.emit();
      }
    });
  }

  addEducation(record?: any) {
    const idx = this.education.length;
    const subjectList = record?.subjectAndGrades || record?.SubjectAndGrades || [];
    const tags: { name: string; score: string }[] = subjectList.map((item: any) => ({
      name: item.subjectName || item.SubjectName || '',
      score: item.grade || item.Grade || ''
    }));
    this.subjectTags[idx] = tags;
    this.subjectTagsOriginal[idx] = JSON.stringify(tags);

    let boardId = record?.boardId || null;
    let lvl = record?.educationLevel ?? null;
    let isBachelors = lvl === 3 || lvl === 4 || String(lvl).toLowerCase().includes('bachelor') || String(lvl).toLowerCase().includes('ug') || String(lvl).toLowerCase().includes('master') || String(lvl).toLowerCase().includes('pg');

    const group = this.fb.group({
      id: [record?.id || null],
      grade: [this.getFriendlyLevelLabel(lvl)],
      level: [lvl, FieldValidators.requiredOnly],
      schoolName: [record?.schoolName || '', !isBachelors ? FieldValidators.schoolName : []],
      city: [record?.city || '', FieldValidators.cityName],
      country: [record?.country || '', FieldValidators.requiredOnly],
      yearsFrom: [this.extractYear(record?.attendedFrom || ''), FieldValidators.yearField],
      yearsTo: [this.extractYear(record?.attendedTo || ''), FieldValidators.yearField],
      boardId: [boardId, !isBachelors ? FieldValidators.requiredOnly : []],
      percentage: [record?.totalGrade || '', !isBachelors ? FieldValidators.percentageScore : []],
      universityName: [record?.schoolName || '', isBachelors ? FieldValidators.personName : []],
      degreeName: [record?.degreeName || '', isBachelors ? FieldValidators.personName : []],
      majors: [record?.majors || '', isBachelors ? FieldValidators.personName : []],
      fileUrl: [record?.documentUrl || null],
    }, { validators: yearRangeValidator });

    group.get('level')?.valueChanges.subscribe((newLevel) => {
      const bachelors = newLevel === 3 || newLevel === 4 || String(newLevel).toLowerCase().includes('bachelor') || String(newLevel).toLowerCase().includes('ug') || String(newLevel).toLowerCase().includes('master') || String(newLevel).toLowerCase().includes('pg');
      group.get('grade')?.setValue(this.getFriendlyLevelLabel(newLevel));

      const schoolControl = group.get('schoolName');
      const uniControl = group.get('universityName');
      const degreeControl = group.get('degreeName');
      const majorsControl = group.get('majors');
      const percentageControl = group.get('percentage');

      if (bachelors) {
        schoolControl?.clearValidators();
        uniControl?.setValidators(FieldValidators.personName);
        degreeControl?.setValidators(FieldValidators.personName);
        majorsControl?.setValidators(FieldValidators.personName);
        percentageControl?.clearValidators();
      } else {
        uniControl?.clearValidators();
        degreeControl?.clearValidators();
        majorsControl?.clearValidators();
        schoolControl?.setValidators(FieldValidators.schoolName);
        percentageControl?.setValidators(FieldValidators.percentageScore);
      }
      const boardControl = group.get('boardId');
      if (bachelors) {
        boardControl?.clearValidators();
      } else {
        boardControl?.setValidators(FieldValidators.requiredOnly);
      }
      boardControl?.updateValueAndValidity({ emitEvent: false });
      schoolControl?.updateValueAndValidity({ emitEvent: false });
      uniControl?.updateValueAndValidity({ emitEvent: false });
      degreeControl?.updateValueAndValidity({ emitEvent: false });
      majorsControl?.updateValueAndValidity({ emitEvent: false });
      percentageControl?.updateValueAndValidity({ emitEvent: false });
    });

    this.education.push(group);
  }

  addSubjectTag(index: number) {
    if (!this.canAddSubject(index)) return;
    const name = (this.subjectInputs[index] || '').trim();
    const score = (this.scoreInputs[index] || '').trim();

    if (!this.subjectTags[index]) {
      this.subjectTags[index] = [];
    }
    this.subjectTags[index] = [...this.subjectTags[index], { name, score }];
    this.subjectInputs[index] = '';
    this.scoreInputs[index] = '';
  }

  isSubjectValid(index: number): boolean {
    const name = (this.subjectInputs[index] || '');
    if (!name) return true; // Don't show error if empty
    return /^[a-zA-Z\s]+$/.test(name);
  }

  isScoreValid(index: number): boolean {
    const score = (this.scoreInputs[index] || '');
    if (!score) return true; // Don't show error if empty
    return /^[0-9]+(\.[0-9]+)?$/.test(score);
  }

  canAddSubject(index: number): boolean {
    const name = (this.subjectInputs[index] || '').trim();
    const score = (this.scoreInputs[index] || '').trim();
    return name.length > 0 && score.length > 0 && this.isSubjectValid(index) && this.isScoreValid(index);
  }

  removeSubjectTag(eduIndex: number, tagIndex: number) {
    this.subjectTags[eduIndex] = this.subjectTags[eduIndex].filter((_, i) => i !== tagIndex);
  }

  removeEducation(index: number) {
    const id = this.education.at(index).get('id')?.value;
    if (!id) {
      this.education.removeAt(index);
      return;
    }
    this.pendingDeleteIndex = index;
    this.showDeleteDialog = true;
  }

  confirmDelete() {
    if (this.pendingDeleteIndex === null) return;
    const index = this.pendingDeleteIndex;
    const id = this.education.at(index).get('id')?.value;
    this.isDeleting = true;
    this.onboardingService.deleteEducation(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.education.removeAt(index);
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Education record removed.' });
      },
      error: () => {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.pendingDeleteIndex = null;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete education record.' });
      }
    });
  }

  cancelDelete() {
    this.showDeleteDialog = false;
    this.pendingDeleteIndex = null;
  }

  // Helper formats
  extractYear(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return !isNaN(d.getTime()) ? String(d.getFullYear()) : '';
  }

  getFriendlyLevelLabel(level: any): string {
    if (level === null || level === undefined) return 'Education';
    const lvlStr = String(level).toLowerCase().trim();
    if (lvlStr === '0' || lvlStr === '10' || lvlStr.includes('10')) return 'Grade 10';
    if (lvlStr === '1' || lvlStr === '11' || lvlStr.includes('11')) return 'Grade 11';
    if (lvlStr === '2' || lvlStr === '12' || lvlStr.includes('12')) return 'Grade 12';
    if (lvlStr === '3' || lvlStr.includes('bachelor') || lvlStr.includes('ug')) return 'Bachelors';
    if (lvlStr === '4' || lvlStr.includes('master') || lvlStr.includes('pg')) return 'Masters';
    if (isNaN(Number(level))) return String(level);
    return 'Education';
  }

  getFileName(url: string | null): string {
    if (!url) return 'No file uploaded';
    try {
      const decoded = decodeURIComponent(url);
      return decoded.split('/').pop()?.split('?')[0] || 'Document';
    } catch {
      return 'Document';
    }
  }

 onFileSelected(event: any, index: number, inputRef: HTMLInputElement) {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const check = validateFile(file);
  if (!check.valid) {
    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
    inputRef.value = '';
    return;
  }

  const eduGroup = this.education.at(index) as FormGroup;
  (eduGroup as any)._selectedFile = file;
  const localUrl = URL.createObjectURL(file);
  eduGroup.get('fileUrl')?.setValue(localUrl);
  eduGroup.get('fileUrl')?.markAsDirty();
  inputRef.value = '';

  // Initialize parse slot then trigger parsing
  if (!this.parseJobData[index]) {
    this.parseJobData[index] = { jobId: '', status: 'pending' };
  }
  this.processFileAndParse(file, index);
}

// ==================== DOCUMENT PARSING FLOW ====================

processFileAndParse(file: File, index: number) {
  this.submitDocumentForParsing(file, index);
}

private submitDocumentForParsing(file: File, index: number) {
  this.docModalFileName = file.name;
  this.activeDocModalIndex = index;
  this.isDocModalVisible = true;

  setTimeout(() => {
    this.docReadingModal?.show(file.name);
  }, 0);

  this.onboardingService.parseDocument(file, 'MARKSHEET').subscribe({
    next: (response: any) => {
      const jobId = response.job_id;
      this.parseJobData[index] = { jobId, status: 'parsing', pollCount: 0 };
      this.startPollingForResult(jobId, index);
    },
    error: (err: any) => {
      console.error('Failed to submit document for parsing:', err);
      this.docReadingModal?.showError();
      if (this.parseJobData[index]) this.parseJobData[index].status = 'error';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to submit document for analysis', life: 3000 });
    }
  });
}

private startPollingForResult(jobId: string, index: number) {
  const maxPolls = 30;
  this.parseJobData[index].pollCount = 0;

  const poll = () => {
    this.parseJobData[index].pollCount = (this.parseJobData[index].pollCount || 0) + 1;
    this.onboardingService.getDocumentParseResult(jobId).subscribe({
      next: (response: any) => {
        const status = (response.status || '').toLowerCase();
        if ((status === 'completed' || status === 'done') && response.result) {
          this.parseJobData[index].status = 'completed';
          this.parseJobData[index].extractedData = response.result;
          this.pendingExtractedData = response.result;
          if (this.activeDocModalIndex === index) { this.docReadingModal?.completeNow(); }
        } else if (status === 'failed' || status === 'error') {
          this.parseJobData[index].status = 'error';
          if (this.activeDocModalIndex === index) { this.docReadingModal?.showError(); }
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to analyze document', life: 3000 });
        } else {
          const pollCount = (this.parseJobData[index].pollCount || 0);
          if (pollCount < maxPolls) {
            const t = setTimeout(poll, 2000); this.pollTimeouts.push(t);
          } else {
            this.parseJobData[index].status = 'error';
            if (this.activeDocModalIndex === index) { this.docReadingModal?.showError(); }
            this.messageService.add({ severity: 'warn', summary: 'Timeout', detail: 'Document analysis took too long.', life: 3000 });
          }
        }
      },
      error: () => {
        const pollCount = (this.parseJobData[index].pollCount || 0);
        if (pollCount < maxPolls) { const t = setTimeout(poll, 2000); this.pollTimeouts.push(t); }
        else { this.parseJobData[index].status = 'error'; if (this.activeDocModalIndex === index) { this.docReadingModal?.showError(); } }
      }
    });
  };
  const t = setTimeout(poll, 1000);
  this.pollTimeouts.push(t);
}

onDocModalComplete(): void {
  this.isDocModalVisible = false;
  if (this.activeDocModalIndex >= 0 && this.pendingExtractedData) {
    this.autoFillEducationFromParsedData(this.activeDocModalIndex, this.pendingExtractedData);
  }
  this.pendingExtractedData = null;
  this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document analyzed! Fields have been auto-filled.', life: 3000 });
}

onDocModalCancel(): void {
  this.pendingExtractedData = null;
  this.isDocModalVisible = false;
}

onDocModalClose(): void {
  this.isDocModalVisible = false;
}

private autoFillEducationFromParsedData(index: number, extractedData: any) {
  const eduGroup = this.education.at(index) as FormGroup;
  if (!eduGroup) return;

  // ---- subjects / chips ----
  const chips: { name: string; score: string }[] = [];
  const subjectsArray = extractedData.subjects || extractedData.subject_and_grades || extractedData.SubjectsAndGrades;
  if (Array.isArray(subjectsArray)) {
    subjectsArray.forEach((sg: any) => {
      const subName = sg.subject_name || sg.SubjectName || sg.subject || '';
      const marks = sg.marks_obtained !== undefined ? sg.marks_obtained : sg.marks;
      const grade = sg.grade || sg.Grade || '';
      let gradeStr = marks !== undefined ? marks.toString() : '';
      if (grade) { gradeStr = gradeStr ? `${gradeStr} (${grade})` : grade; }
      if (subName || gradeStr) { chips.push({ name: subName, score: gradeStr }); }
    });
  } else {
    const subjectsObj = extractedData.subjects || extractedData;
    if (subjectsObj && typeof subjectsObj === 'object') {
      Object.keys(subjectsObj).forEach(key => {
        const val = subjectsObj[key];
        if (val && typeof val === 'object' && !Array.isArray(val) && key !== 'subjects') {
          const marks = val.marks_obtained !== undefined ? val.marks_obtained : val.marks;
          const grade = val.grade || val.Grade;
          if (marks !== undefined || grade !== undefined) {
            let gradeStr = marks !== undefined ? marks.toString() : '';
            if (grade) { gradeStr = gradeStr ? `${gradeStr} (${grade})` : grade; }
            chips.push({ name: key, score: gradeStr });
          }
        }
      });
    }
  }

  if (chips.length > 0) {
  if (!this.subjectTags) this.subjectTags = [];
  // MERGE: existing tags + naye API wale
  const existing = this.subjectTags[index] || [];
  this.subjectTags[index] = [...existing, ...chips];
}

  // ---- percentage ----
  let pct: any = extractedData.percentage || extractedData.overall_percentage || extractedData.total_grade;
  if (pct) {
    if (typeof pct === 'string') pct = pct.replace('%', '').trim();
    eduGroup.get('percentage')?.setValue(pct); eduGroup.get('percentage')?.markAsDirty();
  }

  // ---- years from exam_year ----
  if (extractedData.exam_year) {
    const toYear = parseInt(extractedData.exam_year, 10);
    if (!isNaN(toYear)) {
      eduGroup.get('yearsTo')?.setValue(toYear.toString()); eduGroup.get('yearsTo')?.markAsDirty();
      eduGroup.get('yearsFrom')?.setValue((toYear - 1).toString()); eduGroup.get('yearsFrom')?.markAsDirty();
    }
  }

  // ---- level-specific ----
  const level = eduGroup.get('level')?.value;
  if (level === 3) {
    if (extractedData.university_name) { eduGroup.get('universityName')?.setValue(extractedData.university_name); eduGroup.get('universityName')?.markAsDirty(); }
    if (extractedData.degree_name) { eduGroup.get('degreeName')?.setValue(extractedData.degree_name); eduGroup.get('degreeName')?.markAsDirty(); }
    if (extractedData.major) { eduGroup.get('majors')?.setValue(extractedData.major); eduGroup.get('majors')?.markAsDirty(); }
  } else {
    if (extractedData.school_name) { eduGroup.get('schoolName')?.setValue(extractedData.school_name); eduGroup.get('schoolName')?.markAsDirty(); }
    if (extractedData.exam_type && this.boardOptions) {
      const match = (this.boardOptions as any[]).find((b: any) => b.label?.toLowerCase().includes(extractedData.exam_type.toLowerCase()));
      if (match) { eduGroup.get('boardId')?.setValue(match.value); eduGroup.get('boardId')?.markAsDirty(); }
    }
  }

  if (extractedData.city) { eduGroup.get('city')?.setValue(extractedData.city); eduGroup.get('city')?.markAsDirty(); }
  if (extractedData.country) { eduGroup.get('country')?.setValue(extractedData.country); eduGroup.get('country')?.markAsDirty(); }

  eduGroup.markAsDirty();
}

ngOnDestroy() {
  this.pollTimeouts.forEach(t => clearTimeout(t));
}



  saveEducation(index: number) {
    const eduGroup = this.education.at(index) as FormGroup;
    eduGroup.markAllAsTouched();
    if (eduGroup.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError();
      return;
    }

    const val = eduGroup.value;
    const existingId = val.id;

    const formData = new FormData();
    if (existingId) {
      formData.append('Id', existingId.toString());
    }

    let levelNum = val.level !== null && val.level !== undefined ? val.level : 0;
    formData.append('EducationLevel', levelNum.toString());

    const isBachelors = levelNum === 3 || levelNum === 4 || String(val.grade).toLowerCase().includes('bachelor') || String(val.grade).toLowerCase().includes('master');

    if (isBachelors) {
      formData.append('SchoolName', val.universityName || '');
      formData.append('DegreeName', val.degreeName || '');
      formData.append('Majors', val.majors || '');
      formData.append('TotalGrade', 'N/A');
    } else {
      formData.append('SchoolName', val.schoolName || '');
      // Board ID from dropdown
      if (val.boardId !== null && val.boardId !== undefined) {
        formData.append('BoardId', val.boardId.toString());
      }
      formData.append('TotalGrade', val.percentage || '');
      // Subject tags
      const tags = this.subjectTags[index] || [];
      if (tags.length === 0) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'At least one Subject & Grade is required.' });
        return;
      }
      const parsedSubjects = tags.map(t => ({ SubjectName: t.name, Grade: t.score }));
      formData.append('SubjectAndGrades', JSON.stringify(parsedSubjects));
    }

    formData.append('City', val.city || '');
    formData.append('Country', val.country || '');
    formData.append('AttendedFrom', val.yearsFrom ? `${val.yearsFrom}-01-01` : '');
    formData.append('AttendedTo', val.yearsTo ? `${val.yearsTo}-01-01` : '');

    const selectedFile = (eduGroup as any)._selectedFile;
    if (selectedFile) {
      formData.append('DocumentUrl', selectedFile);
    }

    if (existingId) {
      // Dirty check: skip API call if nothing changed
      const tagsChanged = JSON.stringify(this.subjectTags[index] || []) !== (this.subjectTagsOriginal[index] || '[]');
      if (!eduGroup.dirty && !tagsChanged && !selectedFile) {
        this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
        return;
      }
      this.isSavingMap[index] = true;
      this.onboardingService.updateEducation(existingId, formData).subscribe({
        next: () => {
          this.isSavingMap[index] = false;
          // Update snapshot after successful save
          this.subjectTagsOriginal[index] = JSON.stringify(this.subjectTags[index] || []);
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Education record updated successfully.' });
          eduGroup.markAsPristine();
        },
        error: (err) => {
  this.isSavingMap[index] = false;
  const backendMsg = err?.error?.errors?.documentUrl?.[0] || 'Failed to update award.';
  this.messageService.add({ severity: 'error', summary: 'Error', detail: backendMsg });
}
      });
    } else {
      this.isSavingMap[index] = true;
      this.onboardingService.createEducation(formData).subscribe({
        next: (res: any) => {
          this.isSavingMap[index] = false;
          const newId = res?.id || res?.result?.id;
          if (newId) {
            eduGroup.get('id')?.setValue(newId);
          }
          // Snapshot after creation
          this.subjectTagsOriginal[index] = JSON.stringify(this.subjectTags[index] || []);
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Education record saved successfully.' });
          eduGroup.markAsPristine();
        },
        error: () => {
          this.isSavingMap[index] = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save education record.' });
        }
      });
    }
  }

  previewFile(url: string, name: string = 'Document Preview') {
    if (!url) return;
    this.originalFileUrl = url;
    this.previewFileName = name;
    this.isPdfLoading = true;
    // Blob URLs are local (file selected but not saved yet) — open directly
    // Remote URLs route through Google Docs Viewer
    let viewerUrl: string;
    if (url.startsWith('blob:')) {
      viewerUrl = url;
    } else {
      viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    }
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.displayPreviewDialog = true;
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }
}
